/**
 * The numeric fields a viewpoint offers, and how to read one off a component.
 *
 * Beside {@link getFieldsForViewpoint} rather than in the view that first needed it. Both helpers
 * are about a viewpoint's scope data and nothing about either is specific to a way of drawing: the
 * hierarchical treemap family asked the question first and the 3D network view asks the identical
 * one, which is the point at which a helper stops belonging to a feature.
 */

import { APIFieldType, type ArdoqId } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { PermissionContext } from '@ardoq/access-control';
import { isTruthy } from '@ardoq/common-helpers';
import { uniq } from 'lodash';
import {
  type FieldWithPermissions,
  getFieldsForViewpoint,
} from './getFieldsForViewpoint';

/**
 * Resolved the way the other viewpoint-mode field pickers resolve theirs —
 * {@link getFieldsForViewpoint} deduplicates by name, marks the confidential ones and drops fields
 * belonging to component types this viewpoint did not pull in. `scopeData.fields` holds one entry
 * per field per model, so reading it directly offered the same field once per workspace that
 * defines it (ARD-32072 for the same symptom in the component matrix).
 *
 * Relevance is measured against every component in scope rather than a start set: a view that
 * sizes by a field sizes every mark it draws, not just the components a traversal started from.
 */
export const fieldsInScope = (
  scopeData: EnhancedScopeData,
  permissionContext: PermissionContext,
  types: APIFieldType[]
): FieldWithPermissions[] => {
  const components = Object.values(scopeData.componentsById);
  return getFieldsForViewpoint({
    scopeData,
    permissionContext,
    workspaceIds: uniq(
      components.map(({ rootWorkspace }) => rootWorkspace).filter(isTruthy)
    ),
    types,
    relevantComponentTypeIds: new Set(
      components.map(({ typeId }) => typeId).filter(isTruthy)
    ),
  });
};

export const numericFieldsInScope = (
  scopeData: EnhancedScopeData,
  permissionContext: PermissionContext
): FieldWithPermissions[] =>
  fieldsInScope(scopeData, permissionContext, [APIFieldType.NUMBER]);

/** Reads a component's numeric field value out of the viewpoint's own scope data. */
export const readFieldValueFromScopeData =
  (scopeData: EnhancedScopeData) =>
  (modelId: ArdoqId, fieldName: string): number | null => {
    const value = scopeData.componentsById[modelId]?.[fieldName];
    return typeof value === 'number' ? value : null;
  };
